import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, Flex, Strong, Text } from '@radix-ui/themes';
import { HELP_URLS } from 'classes/helpers/url.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonProgress } from 'components/common/progress';
import { LandingFormWrapper } from 'components/main/landing/forms/wrapper';
import { AuthContext } from 'contexts/auth.context';
import { t } from 'i18next';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

const LOADING_MS = 1_000;
const PROGRESS_INTERVAL_MS = 50;

enum Step {
  Loading,
  Login,
  Error,
  Verification,
}

export const LoginFormAuth0 = () => {
  const authCx = useContext(AuthContext);
  const [progress, setProgress] = useState(0);
  const [step, setStep] = useState(Step.Loading);
  const { user } = useAuth0();

  const needsVerification = authCx.error?.type === 'verification';

  useEffect(() => {
    const progressInterval = setInterval(() => {
      if (progress === 100 && !authCx.loading) {
        clearInterval(progressInterval);

        setTimeout(() => {
          if (!authCx.error) {
            setStep(Step.Login);
            return;
          }

          if (needsVerification) {
            setStep(Step.Verification);
            return;
          }

          setStep(Step.Error);
        }, LOADING_MS / 2);
        return;
      }

      const next = Math.min(100, progress + LOADING_MS / PROGRESS_INTERVAL_MS);
      if (next === progress) {
        return;
      }

      setProgress(next);
    }, PROGRESS_INTERVAL_MS);

    return () => clearInterval(progressInterval);
  }, [progress, authCx.loading, authCx.error]);

  useEffect(() => {
    const error = authCx.error;

    if (error) {
      console.error({
        event: 'LoginFormAuth0: detected an error',
        error,
      });

      if (error.type === 'verification') {
        setStep(Step.Verification);
      } else {
        setStep(Step.Error);
      }
    }
  }, [authCx.error]);

  const renderContent = () => {
    if (authCx.loading || step === Step.Loading) {
      return <CommonProgress color={RADIX.COLOR.INFO} value={progress} />;
    }

    if (step === Step.Verification) {
      const helpCenterUrl = t('common.intercom-url') + HELP_URLS.LANDING;
      return (
        <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
          <Text>
            <Trans
              i18nKey="common.verify-email-prompt"
              t={t}
              values={{
                x: user?.email,
              }}
              components={[<Strong key="0" />]}
            />
          </Text>
          <Text>
            <Trans
              i18nKey="common.verify-email-help"
              t={t}
              components={[<a href={helpCenterUrl} key="0" />]}
            />
          </Text>
        </Flex>
      );
    }

    if (step === Step.Error && authCx.error) {
      return (
        <>
          <Box>
            <Text align="center">
              {authCx.error.message} (
              {(authCx.error.type ?? 'unknown').toUpperCase()}
              ).
            </Text>{' '}
            <Text align="center">Please logout and try again.</Text>
          </Box>
          <Box>
            <Button
              color={RADIX.COLOR.DANGER}
              className="btn-block"
              onClick={() => authCx.logoutAuth0()}
            >
              Logout
            </Button>
          </Box>
        </>
      );
    }

    if (step === Step.Login) {
      return (
        <CommonFormGrid columns={2}>
          <Box>
            <Button
              color={RADIX.COLOR.SUCCESS}
              className="btn-block"
              onClick={() => authCx.loginAuth0()}
            >
              Login
            </Button>
          </Box>
          <Box>
            <Button className="btn-block" onClick={() => authCx.signupAuth0()}>
              Sign Up
            </Button>
          </Box>
        </CommonFormGrid>
      );
    }
  };

  const getHeader = () => {
    if (authCx.loading) {
      return 'Intializing Auth0 client...';
    }

    if (needsVerification) {
      return 'Email Verification Required';
    }

    if (authCx.error) {
      return 'There was a problem with your request.';
    }

    return 'Please use Auth0 to authenticate your session.';
  };

  return (
    <ErrorBoundary componentName="LoginFormAuth0" hideIntercom>
      <LandingFormWrapper header={getHeader()}>
        <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
          {renderContent()}
        </Flex>
      </LandingFormWrapper>
    </ErrorBoundary>
  );
};
